import {
  PROTOCOL_VERSION,
  type AvatarId, type ClientMessageInput, type ErrorCode, type GameEnvelope, type GameId, type HostCommand, type InviteView, type PartySummary, type RoomView, type ServerMessage,
  type SocialOp, type SocialSnapshot,
} from '@gmp/shared-types/browser';
import { SERVER_URL, WS_URL } from './config';
import { getCredentials, type Credentials } from './identity';
import { store } from './storage';
import { awayAtStart, staleAfterAway } from './activity';

export type ConnStatus = 'connecting' | 'online' | 'offline' | 'superseded';

export interface Notice {
  id: number;
  kind: string;
  text: string;
  tone: 'info' | 'good' | 'bad';
}

export interface PartyState {
  status: ConnStatus;
  playerId: string | null;
  room: RoomView | null;
  game: GameEnvelope | null;
  /** Code we are waiting on host approval for. */
  pendingCode: string | null;
  leftReason: 'left' | 'kicked' | 'room_closed' | 'denied' | null;
  error: { code: ErrorCode | 'network'; message: string } | null;
  notices: Notice[];
  /** Round trip in ms (shown as connection quality). */
  rtt: number | null;
  /** Your Turn inbox: every open party this player is in (null until asked). */
  parties: PartySummary[] | null;
  /** Turn alerts on this device, as the server last confirmed (null until asked). */
  push: { available: boolean; on: boolean } | null;
  /** Signed in on this device (optional accounts). */
  signedIn: boolean;
  /** Your account, friends, requests and recent players (signed in only; null until loaded). */
  social: SocialSnapshot | null;
  /** Party invites from friends, live (they expire after 5 minutes). */
  invites: InviteView[];
}

export interface ActResult {
  ok: boolean;
  error?: string;
}

interface Target {
  code: string;
  role: 'player' | 'display';
}

const ROOM_KEY = 'gmp.room';

/** /display/1234 (older links also carry #k=…, which is ignored now). */
/**
 * The party seat to reopen on start. Away from the app for an hour or more: none (you land on Home, and
 * app/awayReset.ts leaves the stale live parties).
 */
function savedSeat(): Target | null {
  if (staleAfterAway(awayAtStart)) {
    store.remove(ROOM_KEY);
    return null;
  }
  return store.get<Target>(ROOM_KEY);
}

function displayTargetFromUrl(): Target | null {
  const m = window.location.pathname.match(/^\/display\/([A-Za-z0-9]{4,6})\/?$/);
  return m ? { code: m[1]!.toUpperCase(), role: 'display' } : null;
}

function newActionId() {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 20);
}

/**
 * The browser's connection to the authoritative game server.
 *  - Reconnects with backoff and resumes the last room automatically (refresh, network drop, phone sleep).
 *  - Every action carries an id and is re-sent after a reconnect until acknowledged; the server applies an
 *    id once, so retries can never double-submit.
 *  - Estimates the server clock offset from ping round trips so every device counts down to the same
 *    server deadline instead of running its own timer.
 */
export class PartyClient {
  private ws: WebSocket | null = null;
  private creds: Credentials | null = null;
  private listeners = new Set<() => void>();
  private pending = new Map<string, { msg: ClientMessageInput; resolve: (r: ActResult) => void; timer: number }>();
  private samples: { offset: number; rtt: number }[] = [];
  private offset = 0;
  private retry = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private noticeSeq = 0;
  /**
   * The room to (re)attach to after every connect. Player seats persist in localStorage so a refresh resumes
   * them; an audience screen's target lives only in this tab, so opening the TV link in the host's own
   * browser never takes over the host's seat.
   */
  private target: Target | null = displayTargetFromUrl() ?? savedSeat();
  private closedByUs = false;
  state: PartyState = { status: 'connecting', playerId: null, room: null, game: null, pendingCode: null, leftReason: null, error: null, notices: [], rtt: null, parties: null, push: null, signedIn: false, social: null, invites: [] };

  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.state.status === 'offline') this.connect();
    });
    window.addEventListener('online', () => this.state.status === 'offline' && this.connect());
  }

  // ------------------------------------------------------------------ store plumbing

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getState = () => this.state;
  private set(patch: Partial<PartyState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  /** Server-synchronized "now" (epoch ms). */
  now = () => Date.now() + this.offset;

  get hasSavedRoom() {
    return !!this.target;
  }

  // ------------------------------------------------------------------ connection

  async connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.closedByUs = false;
    this.set({ status: 'connecting' });
    try {
      this.creds = await getCredentials();
    } catch (e) {
      this.set({ status: 'offline', error: { code: 'network', message: (e as Error).message } });
      return this.scheduleReconnect();
    }
    const ws = new WebSocket(WS_URL);
    this.ws = ws;
    ws.onopen = () => {
      this.send({ type: 'hello', protocol: PROTOCOL_VERSION, token: this.creds!.token });
    };
    ws.onmessage = (ev) => this.onMessage(JSON.parse(String(ev.data)) as ServerMessage);
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (ev.code === 4001) return this.set({ status: 'superseded' });
      this.set({ status: 'offline' });
      if (!this.closedByUs) this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = Math.min(8000, 400 * 2 ** this.retry) * (0.75 + Math.random() * 0.5);
    this.retry++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  /**
   * Signed in or out on this device: drop the socket and reconnect with the new credential (the server
   * re-attaches to the saved party if the new identity is in it, and forgets it otherwise).
   */
  resetIdentity() {
    this.closedByUs = true;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.creds = null;
    this.set({ status: 'connecting', playerId: null, room: null, game: null, pendingCode: null, parties: null, social: null, invites: [], signedIn: false, push: null });
    void this.connect();
  }

  /** Accounts and friends (signed-in only). Resolves with the server's answer (message on success). */
  social(op: SocialOp) {
    return this.request({ type: 'social', actionId: newActionId(), op });
  }

  answerInvite(inviteId: string, accept: boolean, nickname?: string, avatar?: AvatarId) {
    this.set({ invites: this.state.invites.filter((i) => i.id !== inviteId), error: null, leftReason: null });
    return this.social({ op: 'invite_answer', inviteId, accept, nickname, avatar });
  }

  /** Take the session back after "opened somewhere else". */
  reclaim() {
    void this.connect();
  }

  private send(msg: ClientMessageInput) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  private ping() {
    this.send({ type: 'ping', t: performance.now() });
  }

  private onMessage(m: ServerMessage) {
    switch (m.type) {
      case 'welcome': {
        this.retry = 0;
        this.offset = m.serverNow - Date.now();
        this.set({ status: 'online', playerId: m.playerId, error: null, signedIn: 'accountId' in (this.creds ?? {}) });
        if (this.state.signedIn) void this.social({ op: 'get' });
        for (let i = 0; i < 4; i++) setTimeout(() => this.ping(), i * 300);
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = window.setInterval(() => this.ping(), 5000);
        if (this.target) {
          if (this.target.role === 'display') this.send({ type: 'watch_room', code: this.target.code });
          else this.send({ type: 'resume', code: this.target.code });
        }
        for (const p of this.pending.values()) this.send(p.msg);
        return;
      }
      case 'pong': {
        const nowPerf = performance.now();
        const rtt = nowPerf - m.t;
        const offset = m.serverNow + rtt / 2 - Date.now();
        this.samples.push({ offset, rtt });
        if (this.samples.length > 12) this.samples.shift();
        // Median of the lowest-latency samples: robust against a slow packet skewing the clock.
        const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, 5).map((s) => s.offset).sort((a, b) => a - b);
        this.offset = best[Math.floor(best.length / 2)] ?? offset;
        this.set({ rtt: Math.round(rtt) });
        return;
      }
      case 'view':
        if (m.room.you.role !== 'display' && this.target?.code !== m.room.code) {
          this.target = { code: m.room.code, role: 'player' };
          store.set(ROOM_KEY, this.target);
        }
        this.set({ room: m.room, game: m.game, pendingCode: null, leftReason: null, error: null });
        return;
      case 'parties':
        return this.set({ parties: m.parties });
      case 'push_status':
        return this.set({ push: { available: m.available, on: m.on } });
      case 'social':
        return this.set({ social: m.snapshot, invites: m.snapshot?.invites ?? [] });
      case 'presence': {
        const s = this.state.social;
        if (!s) return;
        return this.set({ social: { ...s, friends: s.friends.map((f) => (f.accountId === m.accountId ? { ...f, presence: m.presence } : f)) } });
      }
      case 'invite':
        if (!this.state.invites.some((i) => i.id === m.invite.id)) this.set({ invites: [...this.state.invites.filter((i) => i.from.accountId !== m.invite.from.accountId), m.invite] });
        return;
      case 'invite_closed':
        return this.set({ invites: this.state.invites.filter((i) => i.id !== m.id) });
      case 'tick':
        // Real-time snapshot: game slice only, ~20 per second.
        if (this.state.room) this.set({ game: m.game });
        return;
      case 'ack': {
        const p = this.pending.get(m.actionId);
        if (p) {
          clearTimeout(p.timer);
          this.pending.delete(m.actionId);
          p.resolve({ ok: m.ok, error: m.error });
        }
        return;
      }
      case 'pending':
        this.target = { code: m.code, role: 'player' };
        store.set(ROOM_KEY, this.target);
        return this.set({ pendingCode: m.code, room: null, game: null });
      case 'left':
        this.forgetRoom();
        return this.set({ room: null, game: null, pendingCode: null, leftReason: m.reason });
      case 'notice':
        return this.notify(m.text, m.kind === 'kicked_other' || m.kind === 'left' ? 'info' : m.kind === 'join_request' ? 'good' : 'info');
      case 'error':
        if (m.code === 'superseded') return;
        if (m.code === 'unauthorized' && /expired|Invalid session/.test(m.message)) {
          void getCredentials(true).then((c) => {
            this.creds = c;
            this.send({ type: 'hello', protocol: PROTOCOL_VERSION, token: c.token });
          });
          return;
        }
        if ((m.code === 'room_not_found' || m.code === 'not_member' || m.code === 'banned') && this.target && !this.state.room) {
          this.forgetRoom();
        }
        this.set({ error: { code: m.code, message: m.message } });
        return;
    }
  }

  notify(text: string, tone: Notice['tone'] = 'info') {
    const n = { id: ++this.noticeSeq, kind: 'local', text, tone };
    this.set({ notices: [...this.state.notices.slice(-3), n] });
    setTimeout(() => this.set({ notices: this.state.notices.filter((x) => x.id !== n.id) }), 3500);
  }

  clearError() {
    this.set({ error: null, leftReason: null });
  }

  private forgetRoom() {
    if (this.target?.role !== 'display') store.remove(ROOM_KEY);
    this.target = null;
  }

  // ------------------------------------------------------------------ commands

  createRoom(nickname: string, avatar: AvatarId, gameId?: GameId) {
    this.set({ error: null, leftReason: null });
    this.send({ type: 'create_room', nickname, avatar, gameId });
  }

  joinRoom(code: string, nickname: string, avatar: AvatarId, spectator = false) {
    this.set({ error: null, leftReason: null });
    this.send({ type: 'join_room', code, nickname, avatar, spectator });
  }

  watch(code: string) {
    this.target = { code, role: 'display' };
    if (!this.send({ type: 'watch_room', code })) void this.connect();
  }

  /** Asks the server for the Your Turn inbox (answered with a 'parties' message). */
  myParties() {
    if (!this.send({ type: 'my_parties' })) void this.connect();
  }

  /** Turn alerts: hand this device's push subscription to the server (it answers with push_status). */
  pushSubscribe(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    if (!this.send({ type: 'push_subscribe', subscription })) void this.connect();
  }

  pushUnsubscribe(endpoint: string) {
    this.send({ type: 'push_unsubscribe', endpoint });
  }

  pushCheck(endpoint: string) {
    this.send({ type: 'push_check', endpoint });
  }

  /** Off this party's screen, seat kept (async games carry on; the inbox brings you back). */
  stepOut() {
    this.send({ type: 'step_out' });
    this.forgetRoom();
    this.set({ room: null, game: null, pendingCode: null });
  }

  /** Opens one of your parties from the inbox. */
  openParty(code: string) {
    this.set({ error: null, leftReason: null });
    this.target = { code, role: 'player' };
    store.set(ROOM_KEY, this.target);
    if (!this.send({ type: 'resume', code })) void this.connect();
  }

  /** Gives up your seat in one of your parties by code (answered with your updated 'parties'). */
  leaveParty(code: string) {
    if (this.target?.code === code && this.target.role !== 'display') {
      this.forgetRoom();
      this.set({ room: null, game: null, pendingCode: null });
    }
    this.send({ type: 'leave_party', code });
  }

  /** This device is an audience screen (TV). */
  get isDisplay() {
    return this.target?.role === 'display';
  }

  leave() {
    this.send({ type: 'leave_room' });
    this.forgetRoom();
    this.set({ room: null, game: null, pendingCode: null });
  }

  profile(nickname?: string, avatar?: AvatarId) {
    this.send({ type: 'profile', nickname, avatar });
  }

  ready(ready: boolean) {
    this.send({ type: 'ready', ready });
  }

  report(submissionId: string, reason: string) {
    this.send({ type: 'report', submissionId, reason });
  }

  private request(msg: ClientMessageInput & { actionId: string }): Promise<ActResult> {
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(msg.actionId);
        resolve({ ok: false, error: 'No connection to the game. Try again.' });
      }, 15_000);
      this.pending.set(msg.actionId, { msg, resolve, timer });
      this.send(msg);
    });
  }

  private inputSeq = 0;
  /** Latest control input for real-time games. Fire-and-forget; the server keeps only the newest. */
  input(input: Record<string, unknown>) {
    this.send({ type: 'input', seq: ++this.inputSeq, input });
  }

  act(action: Record<string, unknown>) {
    return this.request({ type: 'game_action', actionId: newActionId(), action });
  }

  host(command: HostCommand) {
    return this.request({ type: 'host', actionId: newActionId(), command });
  }

  /** Grudge Match: opt in to (or back out of) a rematch of the finished game. The server keeps the window. */
  rematch(optIn: boolean) {
    return this.request({ type: 'rematch', actionId: newActionId(), optIn });
  }

  /** Uploads a photo or video for the current round, with progress. The server validates everything. */
  upload(blob: Blob, kind: 'image' | 'video', onProgress?: (fraction: number) => void): Promise<{ mediaId: string } | { error: string }> {
    const code = this.state.room?.code;
    if (!code || !this.creds) return Promise.resolve({ error: 'Not connected to a party' });
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${SERVER_URL}/api/media?code=${code}&kind=${kind}`);
      xhr.setRequestHeader('authorization', `Bearer ${this.creds!.token}`);
      xhr.timeout = 120_000;
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => {
        let body: { mediaId?: string; error?: string } = {};
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* ignore */
        }
        resolve(xhr.status === 200 && body.mediaId ? { mediaId: body.mediaId } : { error: body.error ?? `Upload failed (${xhr.status})` });
      };
      xhr.onerror = () => resolve({ error: 'Upload failed. Check your connection and retry.' });
      xhr.ontimeout = () => resolve({ error: 'Upload timed out. Retry.' });
      xhr.send(blob);
    });
  }
}

export const party = new PartyClient();
